import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./RadioRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ChoiceRef } from "../../choices/BaseChoice";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { RadioIcon } from "../../shared/RadioIcon";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";

interface Props extends RendererProps {
    choice: ChoiceModel;
    name?: string;
    value?: string;
    onSelect?(): void;
    checked?: boolean;
    disabled?: boolean;
}

const RadioRenderer = React.forwardRef(function RadioRendererImpl(props: Props, ref: ChoiceRef) {
    const { choice, checked, gridRendering, onSelect, variable, disabled, language } = props;

    const [hover, setHover] = React.useState(false);
    const theme: any = useTheme();
    const classes = useStyles({ theme });

    React.useImperativeHandle(ref, () => ({
        select() {
            onSelect();
        },
    }));

    if (!gridRendering) {
        return <RadioIcon checked={checked} disabled={disabled} backgroundLess={gridRendering} />;
    }

    const choiceText = removeHtmlTags(translate(choice, "TEXT", language));

    return (
        <div
            id={`${choice.elementId}_INPUT`}
            role="radio"
            aria-checked={checked}
            aria-label={choiceText}
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === " ") {
                    onSelect();
                    event.preventDefault();
                }
                if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                    const choices = variable.choiceList;
                    const index = choices.indexOf(choice) + (event.key === "ArrowRight" ? 1 : -1);
                    if (choices[index]) {
                        const siblingElement = document.getElementById(
                            `${choices[index].elementId}.INPUT`,
                        );
                        if (siblingElement) {
                            siblingElement.focus();
                            siblingElement.click();
                            event.preventDefault();
                        }
                    }
                }
            }}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onClick={() => onSelect()}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            className={classnames(
                classes.radio,
                gridRendering && classes.radioGridCell,
                gridRendering && checked && classes.radioGridCellChecked,
            )}
        >
            <RadioIcon
                checked={checked}
                disabled={disabled}
                hover={hover}
                backgroundLess={gridRendering}
            />
        </div>
    );
});

export default RadioRenderer;
